// ListRow: one record in a list that is not a table, drawn as a slat one tone above what the list
// sits on. ListStack holds the slats and moves them: a row that arrives fades and settles in, one
// that leaves dissolves in place before the rest close the gap, a reorder glides, and a row that
// just landed in a list already on screen gives off light for a moment.
// docs/operations/design-standard.md, "Lists".

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FOCUS, STATE, useExiting, useFlip } from './motion';

/** What the list sits on: the page, or a panel. Rows step one tone up from it. */
export type ListOn = 'ground' | 'panel';
/**
 * `tight`: slats split by 1px of the surface, the stack rounded at its ends (queues, logs,
 * pickers, anything scanned down). `loose`: each row its own card with a gap (rows that hold
 * fields or a lot of content, worked one at a time).
 */
export type ListSpacing = 'tight' | 'loose';
/** A row's state in colour. Always beside a word in the row that says the same. */
export type RowTone = 'raised' | 'accent' | 'good' | 'warn' | 'danger';

const SLAT: Record<ListOn, string> = {
  ground: 'bg-surface-container-low',
  panel: 'bg-surface-container',
};

const TONE: Record<ListOn, Record<RowTone, string>> = {
  ground: {
    raised: 'bg-surface-container',
    accent: 'bg-[color-mix(in_srgb,var(--color-primary,#ffcc33)_10%,var(--surface-container-low))]',
    good: 'bg-[color-mix(in_srgb,theme(colors.green.500)_10%,var(--surface-container-low))]',
    warn: 'bg-[color-mix(in_srgb,theme(colors.amber.500)_10%,var(--surface-container-low))]',
    danger: 'bg-[color-mix(in_srgb,theme(colors.red.500)_12%,var(--surface-container-low))]',
  },
  panel: {
    raised: 'bg-surface-container-high',
    accent: 'bg-[color-mix(in_srgb,var(--color-primary,#ffcc33)_10%,var(--surface-container))]',
    good: 'bg-[color-mix(in_srgb,theme(colors.green.500)_10%,var(--surface-container))]',
    warn: 'bg-[color-mix(in_srgb,theme(colors.amber.500)_10%,var(--surface-container))]',
    danger: 'bg-[color-mix(in_srgb,theme(colors.red.500)_12%,var(--surface-container))]',
  },
};

const BACKDROP: Record<ListOn, string> = {
  ground: 'bg-surface',
  panel: 'bg-surface-container-low',
};

/** The current one in a master and detail list: a step up and the accent edge, as in the table. */
const SELECTED_FILL = 'bg-surface-container-highest';
const SELECTED_EDGE = 'shadow-[inset_3px_0_0_0_var(--color-primary,#ffcc33)]';

// A class, not an inline style: useFlip writes and then clears the inline transition while a
// row glides, and clearing it would also wipe one set here.
const MOTION =
  '[transition:box-shadow_380ms_cubic-bezier(0.2,0,0,1),opacity_150ms_cubic-bezier(0.3,0,1,1),background-color_120ms_cubic-bezier(0.2,0,0,1),--np-layer_120ms_cubic-bezier(0.2,0,0,1)]';

let warnedBoth = false;
const GLOW_MS = 1600;
/** More arrivals than this at once is a set change (a filter widened), not a landing. */
const LANDING_LIMIT = 3;

interface Place {
  on: ListOn;
  spacing: ListSpacing;
  flipKey: string | null;
  leaving: boolean;
  landed: boolean;
  first: boolean;
  last: boolean;
}

// Outside a ListStack a row stands alone: a rounded card on the page.
const ALONE: Place = { on: 'ground', spacing: 'loose', flipKey: null, leaving: false, landed: false, first: true, last: true };
const PlaceContext = createContext<Place>(ALONE);

export interface ListRowProps {
  /** The standard body: a title line and a quieter meta line, both truncating. */
  title?: React.ReactNode;
  meta?: React.ReactNode;
  /** Before the title: an avatar, a mark. */
  leading?: React.ReactNode;
  /** After the title, inside the row's main action: a status tag, a count, a date. */
  trailing?: React.ReactNode;
  /**
   * A body of the row's own (a grid that lines up with a list head). Replaces the standard one.
   * With `to` or `onClick` it sits inside a link or button, so it must be phrasing content
   * (spans, not divs or inputs). A row of fields has neither and takes any content.
   */
  children?: React.ReactNode;
  /** The row opens this route. */
  to?: string;
  /** The row does this (selects it, opens a peek). With `to`, it runs on the link's click too. */
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLLIElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLLIElement>;
  /** Names the main action when the visible text is not enough. */
  'aria-label'?: string;
  /** Controls beside the main action, outside it: a peek key, a remove key. */
  actions?: React.ReactNode;
  /** The current one in a master and detail list. */
  selected?: boolean;
  tone?: RowTone;
  className?: string;
  id?: string;
}

export const ListRow = React.forwardRef<HTMLLIElement, ListRowProps>(function ListRow({
  title, meta, leading, trailing, children, to, onClick, onMouseEnter, onMouseLeave, actions, selected = false, tone,
  className = '', id, 'aria-label': ariaLabel,
}, ref) {
  const place = useContext(PlaceContext);
  const interactive = !!to || !!onClick;
  if (process.env.NODE_ENV !== 'production' && to && onClick && !warnedBoth) {
    warnedBoth = true;
    // eslint-disable-next-line no-console
    console.warn('ListRow: `to` and `onClick` are both set; onClick runs on the link click, it does not replace navigation.');
  }

  const [glow, setGlow] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!place.landed) return;
    setGlow(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setGlow(false), GLOW_MS);
  }, [place.landed]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const fill = selected ? SELECTED_FILL : tone ? TONE[place.on][tone] : SLAT[place.on];
  // One shadow class at a time: the landing glow, the selection edge and rest all set the same
  // property, and two of them on one element resolve by stylesheet order.
  const shadow = glow ? 'z-[1] shadow-glow-soft' : selected ? SELECTED_EDGE : 'shadow-none';
  const radius =
    place.spacing === 'loose'
      ? 'rounded-panel'
      : `${place.first ? 'rounded-t-panel' : ''} ${place.last ? 'rounded-b-panel' : ''}`;

  const body = children ?? (
    <span className="flex min-w-0 items-center gap-3">
      {leading && <span className="flex flex-none items-center">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span
          title={typeof title === 'string' ? title : undefined}
          className="block truncate text-sm font-medium leading-5 text-on-surface"
        >{title}</span>
        {meta && <span title={typeof meta === 'string' ? meta : undefined} className="mt-0.5 block truncate text-xs leading-4 text-on-surface-variant">{meta}</span>}
      </span>
      {trailing && <span className="flex flex-none items-center gap-2">{trailing}</span>}
    </span>
  );

  const main = `block min-w-0 rounded-[inherit] px-3 py-2.5 text-left ${FOCUS} focus-visible:ring-inset`;
  const current = selected ? 'true' : undefined;
  let mainEl: React.ReactNode;
  if (to) {
    mainEl = (
      <Link to={to} onClick={onClick} aria-label={ariaLabel} aria-current={current} tabIndex={place.leaving ? -1 : undefined} className={`${main} text-on-surface no-underline`}>
        {body}
      </Link>
    );
  } else if (onClick) {
    mainEl = (
      <button type="button" onClick={onClick} aria-label={ariaLabel} aria-current={current} tabIndex={place.leaving ? -1 : undefined} className={`${main} w-full`}>
        {body}
      </button>
    );
  } else {
    mainEl = <div className="min-w-0 px-3 py-2.5">{body}</div>;
  }

  return (
    <li
      ref={ref}
      id={id}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // React 17 does not type `inert`; it passes the lowercase attribute through. It takes the
      // actions slot out of the tab order along with aria-hidden, which tabIndex alone cannot.
      {...(place.leaving ? ({ inert: '' } as object) : {})}
      data-flip-key={place.flipKey ?? undefined}
      aria-hidden={place.leaving || undefined}
      className={[
        'relative grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center',
        fill,
        radius,
        interactive ? STATE : '',
        // A row of fields lights while you work in it.
        interactive ? '' : 'focus-within:shadow-glow',
        place.leaving ? 'pointer-events-none opacity-0' : 'opacity-100',
        shadow,
        MOTION,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {mainEl}
      {actions ? <div className="flex flex-none items-center gap-1 pr-2">{actions}</div> : <span />}
    </li>
  );
});

export interface ListStackProps<T> {
  /** `null` while loading: nothing renders, nothing animates. */
  items: readonly T[] | null;
  getKey: (item: T) => string;
  /** One ListRow per item. */
  renderRow: (item: T, index: number) => React.ReactNode;
  on?: ListOn;
  spacing?: ListSpacing;
  /** Column heads above the rows, on the surface itself. */
  head?: React.ReactNode;
  /** The head holds at the top of the nearest scroller. */
  stickyHead?: boolean;
  /** What an empty list says, drawn as a slat. Invite the next action. */
  empty?: React.ReactNode;
  /** The list's accessible name. */
  label?: string;
  className?: string;
}

export function ListStack<T>({
  items, getKey, renderRow, on = 'ground', spacing = 'tight', head, stickyHead = false, empty, label, className = '',
}: ListStackProps<T>) {
  const shown = useExiting(items, getKey);
  const list = useRef<HTMLUListElement>(null);
  useFlip(list, [shown.map((s) => (s.leaving ? `${s.key}~` : s.key)).join('|')]);

  // The landing set, latched per batch of items: only a few new rows in a list that was already
  // on screen. Mutated during render so the first paint of the batch already knows.
  const latch = useRef<{ items: readonly T[] | null; seen: Set<string> | null; landed: Set<string> }>({
    items: null,
    seen: null,
    landed: new Set(),
  });
  if (items && latch.current.items !== items) {
    const keys = items.map(getKey);
    const seen = latch.current.seen;
    const fresh = seen ? keys.filter((k) => !seen.has(k)) : [];
    const overlaps = fresh.length < keys.length;
    latch.current = {
      items,
      seen: new Set(keys),
      landed: overlaps && fresh.length > 0 && fresh.length <= LANDING_LIMIT ? new Set(fresh) : new Set(),
    };
  }
  const landed = latch.current.landed;

  const count = shown.length;
  return (
    <div className={`min-w-0 ${className}`}>
      {head && <div className={stickyHead ? `sticky top-0 z-[2] ${BACKDROP[on]}` : ''}>{head}</div>}
      <ul ref={list} role="list" aria-label={label} className={`m-0 grid list-none p-0 ${spacing === 'tight' ? 'gap-px' : 'gap-2'}`}>
        {items && count === 0 && empty != null ? (
          <li className={`rounded-panel px-3 py-3 text-sm text-on-surface-variant motion-safe:animate-rowLand ${SLAT[on]}`}>{empty}</li>
        ) : (
          shown.map((s, i) => (
            <PlaceContext.Provider
              key={s.key}
              value={{
                on,
                spacing,
                flipKey: s.key,
                leaving: s.leaving,
                landed: landed.has(s.key),
                first: i === 0,
                last: i === count - 1,
              }}
            >
              {renderRow(s.item, i)}
            </PlaceContext.Provider>
          ))
        )}
      </ul>
    </div>
  );
}

export default ListRow;
